import "rc-slider/assets/index.css";
import "rc-tooltip/assets/bootstrap.css";

import Slider from "rc-slider";
import Tooltip from "rc-tooltip";
import React, { ReactNode } from "react";
import { Col } from "react-bootstrap";
import styled from "styled-components";
import Branding from "../../config/Branding";

type FactorSliderProperties = {
    parameter: FactorSliderParameter,
    callback: (paramName: string, value: string) => void,
    value?: number
}

type FactorSliderState = {
    value: number
}

const StyledSlider = styled(Slider)`
    right: 5px;
`;

const Label = styled(Col)`
    text-align: left;
`;

const handleStyle = {
    border: `solid 2px ${Branding.primary}`
};

const dotStyle = {
    borderColor: `${Branding.twentyfouriLime}`
};

const trackStyle = {
    backgroundColor: `${Branding.twentyfouriLime}`
};

// Keeping styling minimal; remove unused constants to satisfy linter


export default class FactorSlider extends React.Component<FactorSliderProperties, FactorSliderState> {

    constructor(props: FactorSliderProperties) {
        super(props);

        const { parameter } = props;
        const defaultValue = parameter.default ?? parameter.minimum;

        this.state = {
            value: props.value !== undefined ? props.value : defaultValue
        };
    }

    public componentDidMount(): void {
        // In controlled mode (value prop provided), do not emit initial default
        if (this.props.value === undefined) {
            this.props.callback(this.props.parameter.name, this.state.value.toString());
        }
    }

    public reset(): void {
        const { parameter } = this.props;
        const defaultValue = parameter.default ?? parameter.minimum;

        this.setState({
            value: defaultValue
        });
    }

    public componentDidUpdate(prevProps: FactorSliderProperties): void {
        if (prevProps.value !== this.props.value && this.props.value !== undefined) {
            this.setState({ value: this.props.value });
        }
    }

    private onSliderChange = (value: number): void => {
        this.setState({ value });
    }

    public onSliderAfterChange = (value: number): void => {
        const { parameter, callback } = this.props;
        callback(parameter.name, value.toString());
    };

    public render(): ReactNode {
        const { parameter } = this.props;
        const { value } = this.state;

        // Calculate step size - use 0.01 for decimal ranges, 1 for integer ranges
        const range = parameter.maximum - parameter.minimum;
        const step = range <= 10 ? 0.01 : 1;

        const formatValue = (val: number): string => {
            return step < 1 ? val.toFixed(2) : Math.round(val).toString();
        };

        return (
            <>
                <Label xs="5" sm="5" style={{
                    fontSize: "14px",
                    color: Branding.white,
                    paddingRight: "10px",
                    display: "flex",
                    alignItems: "center",
                    minHeight: "30px"
                }}>
                    {parameter.label || parameter.name}
                </Label>
                <Col xs="7" sm="7" style={{ paddingLeft: "10px" }}>
                    <div style={{ paddingTop: "5px" }}>
                        <StyledSlider
                            min={parameter.minimum}
                            max={parameter.maximum}
                            value={value}
                            onChange={this.onSliderChange}
                            onAfterChange={this.onSliderAfterChange}
                            step={step}
                            trackStyle={trackStyle}
                            handleStyle={handleStyle}
                            dotStyle={dotStyle}
                            handleRender={(node: React.ReactElement, handleProps: { value: number; dragging: boolean }) => (
                                <Tooltip
                                    overlay={formatValue(handleProps.value)}
                                    placement="top"
                                    visible={handleProps.dragging}
                                >
                                    {node}
                                </Tooltip>
                            )}
                        />
                    </div>
                </Col>
            </>
        );
    }
}
